/**
 * API endpoint'leri — FastApiRuleEngineAS backend'i.
 *
 * Backend route prefix: /rule-engine/*  (bkz. API_CONTRACT.md §1.1)
 *
 * Tüm data BACKEND'den alınır; mock katmanı kaldırıldı
 * (tek kaynak: AS tenant -> PostgreSQL + ClickHouse).
 */
import apiClient from './apiClient';

/* ============================================================
 * Auth / Me
 * ============================================================ */
export const authApi = {
  async me() {
    return apiClient.get('/rule-engine/auth/me');
  },
};

/* ============================================================
 * Dönem Yönetimi
 * ============================================================ */
export const periodsApi = {
  async list(params = {}) {
    return apiClient.get('/rule-engine/periods', { params });
  },

  async get(id) {
    return apiClient.get(`/rule-engine/periods/${id}`);
  },

  async create(payload) {
    return apiClient.post('/rule-engine/periods', payload);
  },

  // Yeni donem olustur + opsiyonel olarak kaynak donemin tum aktif
  // gorevlerini yeni donem altina kopyala. Backend:
  //   POST /rule-engine/periods/{source_id}/clone?clone_tasks=true
  async clone(sourceId, payload, cloneTasks = true) {
    return apiClient.post(
      `/rule-engine/periods/${sourceId}/clone`,
      payload,
      { params: { clone_tasks: cloneTasks } },
    );
  },

  async update(id, payload) {
    return apiClient.put(`/rule-engine/periods/${id}`, payload);
  },

  async delete(id) {
    return apiClient.delete(`/rule-engine/periods/${id}`);
  },

  async activate(id) {
    return apiClient.post(`/rule-engine/periods/${id}/activate`);
  },

  async close(id) {
    return apiClient.post(`/rule-engine/periods/${id}/close`);
  },

  async reopen(id) {
    return apiClient.post(`/rule-engine/periods/${id}/reopen`);
  },

  async evaluate(id, opts = {}) {
    const dryRun = Boolean(opts.dry_run ?? opts.dryRun);
    return apiClient.post(
      `/rule-engine/periods/${id}/evaluate`,
      null,
      { params: { dry_run: dryRun } },
    );
  },
};

/* ============================================================
 * Görev (Rule Set) Yönetimi
 * ============================================================ */
export const tasksApi = {
  async list(params = {}) {
    return apiClient.get('/rule-engine/tasks', { params });
  },

  async get(id) {
    return apiClient.get(`/rule-engine/tasks/${id}`);
  },

  async create(payload) {
    return apiClient.post('/rule-engine/tasks', payload);
  },

  async update(id, payload) {
    return apiClient.put(`/rule-engine/tasks/${id}`, payload);
  },

  async remove(id) {
    return apiClient.delete(`/rule-engine/tasks/${id}`);
  },

  async cancel(id, payload) {
    // payload: { policy: 'FULL_POINTS' | 'CANCEL_REVOKE' | 'REPLACE_RECALC', replacement_task_id? }
    return apiClient.post(`/rule-engine/tasks/${id}/cancel`, payload);
  },

  async recalculate(id) {
    // Görevi iptal etmeden dönem evaluation'ını yeniden tetikler.
    return apiClient.post(`/rule-engine/tasks/${id}/recalculate`);
  },

  /**
   * GET /rule-engine/tasks/{id}/user-states — görevin kullanıcı durum dökümü.
   * Admin paneldeki "göz" ikonundan açılan modal bu endpoint'i çağırır.
   *
   * params: { page?, size?, q?, status?, sort? }
   *   status: 'all' | 'scored' | 'zero' | 'null'
   *   sort:   'total_points_desc' | 'total_points_asc' |
   *           'raw_value_desc'    | 'raw_value_asc' |
   *           'kullanici_kodu_asc'
   *
   * Response:
   *   data.rows[]   — { kullanici_kodu, raw_value, points, bonus_points, total_points, tier_index, tier_label }
   *   data.meta     — { task_name, metric_type, stats: {...}, tier_breakdown: [...] }
   *   pagination    — { total, page, size }
   */
  async userStates(id, params = {}) {
    return apiClient.get(`/rule-engine/tasks/${id}/user-states`, { params });
  },

  /**
   * GET /rule-engine/tasks/{id}/user-states/{kodu}/detail
   * Bir görevde bir kullanıcının hesabına giren CH ham satırlarını döner.
   *
   * params: { leg?, limit? }
   *   leg:   'numerator' | 'denominator' (sadece RATIO görevde)
   *   limit: default 200, max 1000
   *
   * Response: { task_name, source_table, applied_filters, columns, rows, ... }
   */
  async userStateDetail(id, kullaniciKodu, params = {}) {
    return apiClient.get(
      `/rule-engine/tasks/${id}/user-states/${encodeURIComponent(kullaniciKodu)}/detail`,
      { params },
    );
  },

  /**
   * GET /rule-engine/tasks/{id}/user-states/export — görev × kullanıcı durum
   * dökümünü Excel (xlsx) olarak indirir. Sayfalama yok; ekrandaki filtrelerle
   * (q/status/sort) tutarlı tüm satırlar iner.
   * params: { q?, status?, sort? }
   */
  async exportUserStates(id, params = {}) {
    return apiClient.get(`/rule-engine/tasks/${id}/user-states/export`, {
      params,
      responseType: 'blob',
    });
  },

  /**
   * GET /rule-engine/tasks/{id}/user-states/{kodu}/detail?export=excel — bir
   * kullanıcının CH ham veri detayını Excel olarak indirir (filtreye uyan tüm
   * satırlar, sayfalama yok).
   * params: { leg?, mode? } — export=excel otomatik eklenir.
   */
  async exportUserStateDetail(id, kullaniciKodu, params = {}) {
    return apiClient.get(
      `/rule-engine/tasks/${id}/user-states/${encodeURIComponent(kullaniciKodu)}/detail`,
      { params: { ...params, export: 'excel' }, responseType: 'blob' },
    );
  },

  /**
   * POST /rule-engine/tasks/{id}/manual-scores — bulk MANUAL-metric push.
   * payload = {entries: [{user_code, raw_value?, points?}], note?}
   */
  async pushManualScores(id, payload) {
    return apiClient.post(`/rule-engine/tasks/${id}/manual-scores`, payload);
  },

  /**
   * POST /rule-engine/tasks/{id}/image — görsel yükle / güncelle.
   * file: File nesnesi (JPG/PNG/WebP/GIF, max 5 MB)
   */
  async uploadImage(id, file) {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post(`/rule-engine/tasks/${id}/image`, fd, {
      headers: { 'Content-Type': undefined },
    });
  },

  /**
   * DELETE /rule-engine/tasks/{id}/image — görsel sil.
   */
  async deleteImage(id) {
    return apiClient.delete(`/rule-engine/tasks/${id}/image`);
  },
};

/* ============================================================
 * Sıralama (Leaderboard)
 * ============================================================ */
export const leaderboardApi = {
  async sks(params = {}) {
    return apiClient.get('/rule-engine/leaderboard/sks', { params });
  },

  async manager(params = {}) {
    return apiClient.get('/rule-engine/leaderboard/manager', { params });
  },

  async exportExcel(params = {}) {
    const p = { ...params, ...(params.tab ? { tab: String(params.tab).toUpperCase() } : {}) };
    return apiClient.get('/rule-engine/leaderboard/export', { params: p, responseType: 'blob' });
  },

  /** GET /rule-engine/leaderboard/user-detail — görev bazlı puan + ödüller */
  async userDetail(params = {}) {
    return apiClient.get('/rule-engine/leaderboard/user-detail', { params });
  },
};

/* ============================================================
 * NOT: Kullanıcılar (Users) endpointleri bu micro app'ten kaldırıldı.
 * Kullanıcı listesi üst seviye "Başa" modülü üzerinden sağlanıyor; rule
 * engine değerlendirmelerinde `public.as_user_registry` okunmaya devam
 * ediliyor ama o okuma backend worker'ının sorumluluğunda.
 * ============================================================ */

/* ============================================================
 * Sistem Parametreleri
 * ============================================================ */
export const parametersApi = {
  async list(params = {}) {
    return apiClient.get('/rule-engine/parameters', { params });
  },

  async create(payload) {
    return apiClient.post('/rule-engine/parameters', payload);
  },

  async update(id, payload) {
    return apiClient.put(`/rule-engine/parameters/${id}`, payload);
  },

  async remove(id) {
    return apiClient.delete(`/rule-engine/parameters/${id}`);
  },
};

/* ============================================================
 * Dashboard & Metadata
 * ============================================================ */
export const dashboardApi = {
  async stats(periodId) {
    const params = periodId ? { period_id: periodId } : {};
    return apiClient.get('/rule-engine/dashboard', { params });
  },
};

export const metadataApi = {
  async chTables() {
    return apiClient.get('/rule-engine/metadata/ch/tables');
  },
  async chColumns(table) {
    return apiClient.get(`/rule-engine/metadata/ch/tables/${table}/columns`);
  },
  // Bir kolonun CH'deki DISTINCT degerlerini doner — admin filter row'da
  // elle "Yürürlükte" yazmak yerine gercek degerlerden secim yapabilsin.
  // Backend tenant-scope'ta SELECT DISTINCT ... LIMIT 100 calistirir.
  async chColumnDistinct(table, column, limit = 100) {
    return apiClient.get(
      `/rule-engine/metadata/ch/tables/${table}/columns/${column}/distinct`,
      { params: { limit } },
    );
  },
};

/* ============================================================
 * Referans Verileri (dropdown kaynakları) — A endpoint'i
 * GET /rule-engine/segments     → CDP segment kataloğu
 * GET /rule-engine/mudurlukler  → DISTINCT sorumlu_mudurluk listesi
 * ============================================================ */
export const referencesApi = {
  async segments(params = {}) {
    return apiClient.get('/rule-engine/segments', { params });
  },
  async mudurlukler(params = {}) {
    return apiClient.get('/rule-engine/mudurlukler', { params });
  },
};

/* ============================================================
 * Evaluation Jobs (bulk evaluate + rollback) — C + D endpoint'leri
 * ============================================================ */
export const jobsApi = {
  /** POST /rule-engine/evaluate-all — aktif tüm dönemleri kuyruğa at. */
  async evaluateAll() {
    return apiClient.post('/rule-engine/evaluate-all');
  },
  /** GET /rule-engine/jobs/{job_id} — işin durumu ({status, done, error, …}). */
  async get(jobId) {
    return apiClient.get(`/rule-engine/jobs/${jobId}`);
  },
  /** DELETE /rule-engine/jobs/{job_id} — başarılı bir job'u geri al. */
  async rollback(jobId) {
    return apiClient.delete(`/rule-engine/jobs/${jobId}`);
  },
};

/* ============================================================
 * Audit Log (admin)
 * ============================================================ */
export const auditApi = {
  /**
   * GET /rule-engine/audit — denetim kayıtları (filtreli, sayfalı).
   * Filtreler: entity, action, user_id, page, size.
   */
  async list(params = {}) {
    return apiClient.get('/rule-engine/audit', { params });
  },
};

/* ============================================================
 * Users (tenant-scoped, read-only)
 * Kaynak: public.as_user_registry — as-cdp-worker tarafından yazılır.
 * Backend tenant izolasyonunu zorunlu kılar; listedeki kullanıcılar
 * yalnızca o tenant'a aittir. Manuel skor atama için kullanılır.
 * ============================================================ */
export const usersApi = {
  /** GET /rule-engine/users — params: { search?, kullanici_tipi?, page?, size? } */
  async list(params = {}) {
    return apiClient.get('/rule-engine/users', { params });
  },
  /** GET /rule-engine/users/:kodu */
  async get(kullanici_kodu) {
    return apiClient.get(`/rule-engine/users/${encodeURIComponent(kullanici_kodu)}`);
  },
};

/* ============================================================
 * Rozetler (Badges) — admin yükler, dönem ödüllerine atanır.
 * Backend: routers/badges.py
 * ============================================================ */
export const badgesApi = {
  /** GET /rule-engine/badges — tüm rozet listesi */
  async list() {
    return apiClient.get('/rule-engine/badges');
  },
  /** POST /rule-engine/badges/upload — multipart dosya yükle (require_admin) */
  async upload(file, name) {
    const fd = new FormData();
    fd.append('file', file);
    if (name) fd.append('name', name.trim());
    return apiClient.post('/rule-engine/badges/upload', fd, {
      headers: { 'Content-Type': undefined },
    });
  },
  /** PATCH /rule-engine/badges/{id} — rozet adını güncelle (require_admin) */
  async update(id, name) {
    return apiClient.patch(`/rule-engine/badges/${id}`, { name });
  },
  /** DELETE /rule-engine/badges/{id} — rozet sil (require_admin) */
  async remove(id) {
    return apiClient.delete(`/rule-engine/badges/${id}`);
  },
};

export default {
  authApi,
  periodsApi,
  tasksApi,
  leaderboardApi,
  parametersApi,
  dashboardApi,
  metadataApi,
  referencesApi,
  jobsApi,
  auditApi,
  usersApi,
  badgesApi,
};
